/* side-nav-visibility.tsx generated by @compiled/babel-plugin v0.29.1 */
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import "./side-nav-visibility.compiled.css";
import { ax, ix } from "@compiled/react/runtime";
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { bind } from 'bind-event-listener';
import { IconButton } from '@atlaskit/button/new';
import __noop from '@atlaskit/ds-lib/noop';
import SidebarCollapseIcon from '@atlaskit/icon/core/sidebar-collapse';
import SidebarExpandIcon from '@atlaskit/icon/core/sidebar-expand';
var SideNavDesktopVisibility = /*#__PURE__*/createContext(null);
var SideNavMobileVisibility = /*#__PURE__*/createContext(false);
/**
 * Sets the visibility of the side nav on desktop screens.
 * Internally exporting for use in tests.
 */
export var SetSideNavDesktopVisibility = /*#__PURE__*/createContext(__noop);
/**
 * Sets the visibility of the side nav for mobile screens
 * Internally exporting for use in tests.
 */
export var SetSideNavMobileVisibility = /*#__PURE__*/createContext(__noop);

/**
 * Manages the side nav visibility state and provides the context.
 */
export var SideNavVisibilityProvider = function SideNavVisibilityProvider(_ref) {
  var children = _ref.children;
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    visibleOnDesktop = _useState2[0],
    setVisibleOnDesktop = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    visibleOnMobile = _useState4[0],
    setVisibleOnMobile = _useState4[1];
  return /*#__PURE__*/React.createElement(SideNavDesktopVisibility.Provider, {
    value: visibleOnDesktop
  }, /*#__PURE__*/React.createElement(SetSideNavDesktopVisibility.Provider, {
    value: setVisibleOnDesktop
  }, /*#__PURE__*/React.createElement(SideNavMobileVisibility.Provider, {
    value: visibleOnMobile
  }, /*#__PURE__*/React.createElement(SetSideNavMobileVisibility.Provider, {
    value: setVisibleOnMobile
  }, children))));
};

/**
 * __useToggleSideNav__
 *
 * Returns a function to toggle the side nav visibility.
 *
 * It will toggle the appropriate internal desktop or mobile side nav visibility state based on the current screen size.
 */
export var useToggleSideNav = function useToggleSideNav() {
  var setVisibleOnDesktop = useContext(SetSideNavDesktopVisibility);
  var setVisibleOnMobile = useContext(SetSideNavMobileVisibility);
  var toggleSideNav = useCallback(function () {
    var _window$matchMedia = window.matchMedia('(min-width: 64rem)'),
      matches = _window$matchMedia.matches;
    if (matches) {
      setVisibleOnDesktop(function (prev) {
        return !prev;
      });
    } else {
      setVisibleOnMobile(function (prev) {
        return !prev;
      });
    }
  }, [setVisibleOnDesktop, setVisibleOnMobile]);
  return toggleSideNav;
};
export var useSideNavVisibility = function useSideNavVisibility() {
  var _ref2 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    _ref2$defaultCollapse = _ref2.defaultCollapsed,
    defaultCollapsed = _ref2$defaultCollapse === void 0 ? false : _ref2$defaultCollapse;
  var visibleOnDesktop = useContext(SideNavDesktopVisibility);
  var setVisibleOnDesktop = useContext(SetSideNavDesktopVisibility);
  var visibleOnMobile = useContext(SideNavMobileVisibility);
  var setVisibleOnMobile = useContext(SetSideNavMobileVisibility);
  return {
    visibleOnDesktop: visibleOnDesktop === null ? !defaultCollapsed : visibleOnDesktop,
    visibleOnMobile: visibleOnMobile,
    setVisibleOnDesktop: setVisibleOnDesktop,
    setVisibleOnMobile: setVisibleOnMobile
  };
};
var styles = {
  hiddenMobileAndDesktop: "_1e0cglyw",
  hiddenMobileOnly: "_1e0cglyw _dm2518uv",
  hiddenDesktopOnly: "_dm25glyw",
  absolutelyPositionWithinSideNav: "_kqswstnw _rjxputpp _152tutpp _1pbyb4wl _bfhkvuon"
};

/**
 * __SideNavToggleButton__
 *
 * Button for toggling the side nav. Should be used in the top bar.
 *
 * The alternative is `SideNavCollapseButton`, which is for use within the side nav.
 *
 * _It contains built-in responsive visibility behaviour:_
 * - For mobile screens, the button is always shown.
 * - For desktop screens, the button is only shown when the side nav is hidden.
 */
export var SideNavToggleButton = function SideNavToggleButton(_ref3) {
  var _ref3$defaultCollapse = _ref3.defaultCollapsed,
    defaultCollapsed = _ref3$defaultCollapse === void 0 ? false : _ref3$defaultCollapse,
    testId = _ref3.testId,
    interactionName = _ref3.interactionName;
  var _useSideNavVisibility = useSideNavVisibility({
      defaultCollapsed: defaultCollapsed
    }),
    isSideNavVisibleOnDesktop = _useSideNavVisibility.visibleOnDesktop,
    isSideNavVisibleOnMobile = _useSideNavVisibility.visibleOnMobile;
  var _useState5 = useState(!defaultCollapsed),
    _useState6 = _slicedToArray(_useState5, 2),
    isSideNavVisible = _useState6[0],
    setIsSideNavVisible = _useState6[1];
  useEffect(function () {
    var _window$matchMedia2 = window.matchMedia('(min-width: 64rem)'),
      matches = _window$matchMedia2.matches;
    setIsSideNavVisible(matches ? isSideNavVisibleOnDesktop : isSideNavVisibleOnMobile);
  }, [isSideNavVisibleOnDesktop, isSideNavVisibleOnMobile]);

  // When screen size changes, ensure we use the correct visibility state
  useEffect(function () {
    var mediaQueryList = window.matchMedia('(min-width: 64rem)');
    return bind(mediaQueryList, {
      type: 'change',
      listener: function listener() {
        setIsSideNavVisible(mediaQueryList.matches ? isSideNavVisibleOnDesktop : isSideNavVisibleOnMobile);
      }
    });
  }, [isSideNavVisibleOnDesktop, isSideNavVisibleOnMobile]);
  var toggleVisibility = useToggleSideNav();
  return /*#__PURE__*/React.createElement("div", {
    "data-testid": testId ? "".concat(testId, "-container") : undefined,
    className: ax([isSideNavVisibleOnDesktop && styles.hiddenDesktopOnly])
  }, /*#__PURE__*/React.createElement(IconButton, {
    appearance: "subtle",
    label: isSideNavVisible ? 'Collapse sidebar' : 'Expand sidebar',
    icon: isSideNavVisible ? SidebarCollapseIcon : SidebarExpandIcon,
    onClick: toggleVisibility,
    testId: testId,
    isTooltipDisabled: false,
    interactionName: interactionName
  }));
};

/**
 * __SideNavCollapseButton__
 *
 * The button for collapsing the side nav, for use within the `<SideNav>`.
 * Should be used within the `collapseButton` slot prop of the `SideNav` component, e.g.
 * ```tsx
 * <SideNav collapseButton={<SideNavCollapseButton label="Collapse sidebar" />} />
 * ```
 *
 * The alternative is `SideNavToggleButton`, which is for use within the top bar.
 *
 * It is only shown on desktop screens, when the side nav is visible.
 */
export var SideNavCollapseButton = function SideNavCollapseButton(_ref4) {
  var testId = _ref4.testId,
    interactionName = _ref4.interactionName;
  var isSideNavVisibleOnDesktop = useContext(SideNavDesktopVisibility);
  var setSideNavVisibleOnDesktop = useContext(SetSideNavDesktopVisibility);
  var collapseSideNav = useCallback(function () {
    setSideNavVisibleOnDesktop(false);
  }, [setSideNavVisibleOnDesktop]);
  return /*#__PURE__*/React.createElement("div", {
    "data-testid": testId ? "".concat(testId, "-container") : undefined,
    className: ax([styles.absolutelyPositionWithinSideNav, isSideNavVisibleOnDesktop && styles.hiddenMobileOnly, !isSideNavVisibleOnDesktop && styles.hiddenMobileAndDesktop])
  }, /*#__PURE__*/React.createElement(IconButton, {
    appearance: "subtle",
    label: "Collapse sidebar",
    icon: SidebarCollapseIcon,
    onClick: collapseSideNav,
    testId: testId,
    isTooltipDisabled: false,
    interactionName: interactionName
  }));
};